import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API = "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";
const MODELS_API = "https://openrouter.ai/api/v1/models?output_modalities=text&supported_parameters=structured_outputs";
const MAX_RULES = 12;
const MAX_EMAILS = 10;
const MIN_ZONE_SIZE = 0.02;
const MIN_POLYGON_AREA = MIN_ZONE_SIZE * MIN_ZONE_SIZE;
const MAX_POLYGON_POINTS = 24;
const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
type AiWeekday = typeof WEEKDAYS[number];

export type AiMonitoringPoint = { x: number; y: number };
export type AiMonitoringRectangleZone = { x: number; y: number; width: number; height: number };
export type AiMonitoringPolygonZone = {
  type: "polygon";
  points: AiMonitoringPoint[];
  x: number;
  y: number;
  width: number;
  height: number;
};
export type AiMonitoringZone = AiMonitoringRectangleZone | AiMonitoringPolygonZone;
export type RoutingPreference = "balanced" | "lowest_cost" | "fastest" | "highest_accuracy";
export type AiModelConfig = {
  mode: "recommended" | "manual";
  primaryModel: string | null;
  fallbackModels: string[];
  routingPreference: RoutingPreference;
  allowFallback: boolean;
};
export type AiMonitoringSchedule = {
  enabled: boolean;
  timezone: string;
  days: AiWeekday[];
  start: string;
  end: string;
};
export type AiMonitoringRule = {
  id: string;
  name: string;
  enabled: boolean;
  instruction: string;
  alertEmails: string[];
  schedule: AiMonitoringSchedule;
  zone: AiMonitoringZone | null;
  minimumConfidence: number;
  cooldownSeconds: number;
  severity: "low" | "medium" | "high" | "critical";
  frameCount: number;
  modelConfig: AiModelConfig;
};
export type AiMonitoringConfig = { version: 1; enabled: boolean; rules: AiMonitoringRule[] };

type Camera = {
  cameraId: string;
  name?: string;
  thumbnailUrl?: string;
  aiMonitoringConfig?: unknown;
  [key: string]: unknown;
};
type Props = { camera: Camera; onUpdateCamera: (camera: any) => void };
type Point = AiMonitoringPoint;
type Model = { id: string; name: string; promptPrice: number | null; completionPrice: number | null };
type AiTestState = {
  requestId?: string;
  status?: string;
  result?: {
    ruleMatched?: boolean;
    notify?: boolean;
    confidence?: number;
    summary?: string;
    observations?: string[];
    modelUsed?: string;
    fallbackUsed?: boolean;
    processingTimeMs?: number;
    frameCount?: number;
  };
  error?: string;
};

type ModelApiItem = {
  id?: unknown;
  name?: unknown;
  expiration_date?: unknown;
  architecture?: { input_modalities?: unknown; output_modalities?: unknown };
  supported_parameters?: unknown;
  pricing?: { prompt?: unknown; completion?: unknown };
};

const button: React.CSSProperties = {
  minHeight: 38,
  padding: "8px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
  color: "#334155",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
};
const input: React.CSSProperties = {
  width: "100%",
  minHeight: 40,
  padding: "9px 11px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
  color: "#0f172a",
  font: "inherit",
  fontSize: 12,
  boxSizing: "border-box",
};

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
function clamp(value: number, minimum = 0, maximum = 1) {
  return Math.min(maximum, Math.max(minimum, value));
}
function round(value: number) {
  return Math.round(value * 1_000_000) / 1_000_000;
}
function stringValue(value: unknown, fallback = "") {
  return String(value ?? "").trim() || fallback;
}
function normalizeEmails(value: unknown) {
  const values = Array.isArray(value) ? value : [];
  return [...new Set(values.map((item) => String(item || "").trim().toLowerCase()))]
    .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    .slice(0, MAX_EMAILS);
}
function defaultTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
function isValidTimeZone(value: string) {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
}
function validClock(value: unknown) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""));
}
function normalizeSchedule(value: unknown): AiMonitoringSchedule {
  const source = objectValue(value);
  const requestedDays = new Set(
    (Array.isArray(source.days) ? source.days : WEEKDAYS).map((item) =>
      String(item || "").toUpperCase()),
  );
  const timezoneCandidate = stringValue(source.timezone, defaultTimeZone());
  return {
    enabled: source.enabled === true,
    timezone: isValidTimeZone(timezoneCandidate) ? timezoneCandidate : defaultTimeZone(),
    days: WEEKDAYS.filter((day) => requestedDays.has(day)),
    start: validClock(source.start) ? String(source.start) : "18:00",
    end: validClock(source.end) ? String(source.end) : "06:00",
  };
}
function polygonArea(points: Point[]) {
  let total = 0;
  for (let index = 0; index < points.length; index += 1) {
    const next = points[(index + 1) % points.length];
    total += points[index].x * next.y - next.x * points[index].y;
  }
  return Math.abs(total) / 2;
}
function polygonZone(value: Point[]): AiMonitoringPolygonZone | null {
  const points: Point[] = [];
  for (const raw of value) {
    const x = Number(raw?.x);
    const y = Number(raw?.y);
    if (![x, y].every(Number.isFinite)) return null;
    const point = { x: round(clamp(x)), y: round(clamp(y)) };
    const previous = points[points.length - 1];
    if (!previous || previous.x !== point.x || previous.y !== point.y) points.push(point);
  }
  if (
    points.length > 1 &&
    points[0].x === points[points.length - 1].x &&
    points[0].y === points[points.length - 1].y
  ) {
    points.pop();
  }
  if (points.length < 3 || points.length > MAX_POLYGON_POINTS) return null;
  if (new Set(points.map((point) => `${point.x}:${point.y}`)).size !== points.length) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const width = Math.max(...xs) - x;
  const height = Math.max(...ys) - y;
  if (width < MIN_ZONE_SIZE || height < MIN_ZONE_SIZE || polygonArea(points) < MIN_POLYGON_AREA) return null;
  return {
    type: "polygon",
    points,
    x: round(x),
    y: round(y),
    width: round(width),
    height: round(height),
  };
}
function normalizeZone(value: unknown): AiMonitoringZone | null {
  const source = objectValue(value);
  const rawPoints = Array.isArray(source.points) ? source.points : [];
  if (source.type === "polygon" || rawPoints.length) {
    const points = rawPoints.map((raw) => {
      const point = objectValue(raw);
      return { x: Number(point.x), y: Number(point.y) };
    });
    return polygonZone(points);
  }

  const x = Number(source.x);
  const y = Number(source.y);
  const width = Number(source.width);
  const height = Number(source.height);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  const nx = clamp(x);
  const ny = clamp(y);
  const nw = clamp(width, 0, 1 - nx);
  const nh = clamp(height, 0, 1 - ny);
  if (nw < MIN_ZONE_SIZE || nh < MIN_ZONE_SIZE) return null;
  return { x: round(nx), y: round(ny), width: round(nw), height: round(nh) };
}
function zonePoints(zone: AiMonitoringZone): Point[] {
  if ("type" in zone && zone.type === "polygon") return zone.points;
  return [
    { x: zone.x, y: zone.y },
    { x: zone.x + zone.width, y: zone.y },
    { x: zone.x + zone.width, y: zone.y + zone.height },
    { x: zone.x, y: zone.y + zone.height },
  ];
}
function svgPoints(points: Point[]) {
  return points.map((point) => `${round(point.x * 1000)},${round(point.y * 1000)}`).join(" ");
}
function newId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? `ai-rule-${crypto.randomUUID()}`
    : `ai-rule-${Date.now()}`;
}
function emptyRule(index: number): AiMonitoringRule {
  return {
    id: newId(),
    name: `AI monitoring rule ${index + 1}`,
    enabled: true,
    instruction: "",
    alertEmails: [],
    schedule: {
      enabled: false,
      timezone: defaultTimeZone(),
      days: [...WEEKDAYS],
      start: "18:00",
      end: "06:00",
    },
    zone: null,
    minimumConfidence: 0.75,
    cooldownSeconds: 120,
    severity: "high",
    frameCount: 6,
    modelConfig: {
      mode: "recommended",
      primaryModel: null,
      fallbackModels: [],
      routingPreference: "balanced",
      allowFallback: true,
    },
  };
}
function normalizeModelConfig(value: unknown): AiModelConfig {
  const source = objectValue(value);
  const mode = source.mode === "manual" ? "manual" : "recommended";
  const routing = ["lowest_cost", "fastest", "highest_accuracy"].includes(String(source.routingPreference))
    ? (source.routingPreference as RoutingPreference)
    : "balanced";
  const primaryModel = stringValue(source.primaryModel) || null;
  const fallbackModels = [...new Set(
    (Array.isArray(source.fallbackModels) ? source.fallbackModels : [])
      .map((item) => stringValue(item))
      .filter((item) => item && item !== primaryModel),
  )].slice(0, 3);
  return {
    mode,
    primaryModel: mode === "manual" ? primaryModel : null,
    fallbackModels: mode === "manual" ? fallbackModels : [],
    routingPreference: routing,
    allowFallback: source.allowFallback !== false,
  };
}
function normalizeRule(value: unknown, index: number): AiMonitoringRule {
  const source = objectValue(value);
  const confidence = Number(source.minimumConfidence);
  const cooldown = Number(source.cooldownSeconds);
  const frames = Number(source.frameCount);
  return {
    id: stringValue(source.id, `ai-rule-${index + 1}`),
    name: stringValue(source.name, `AI monitoring rule ${index + 1}`),
    enabled: source.enabled !== false,
    instruction: String(source.instruction || "").trim().slice(0, 2000),
    alertEmails: normalizeEmails(source.alertEmails),
    schedule: normalizeSchedule(source.schedule),
    zone: normalizeZone(source.zone),
    minimumConfidence: Number.isFinite(confidence) ? clamp(confidence, 0.5, 0.99) : 0.75,
    cooldownSeconds: Number.isFinite(cooldown) ? Math.round(clamp(cooldown, 0, 86400)) : 120,
    severity: ["low", "medium", "high", "critical"].includes(String(source.severity))
      ? (source.severity as AiMonitoringRule["severity"])
      : "high",
    frameCount: Number.isFinite(frames) ? Math.round(clamp(frames, 1, 12)) : 6,
    modelConfig: normalizeModelConfig(source.modelConfig),
  };
}

export function normalizeAiMonitoringConfig(value: unknown): AiMonitoringConfig {
  const source = objectValue(value);
  return {
    version: 1,
    enabled: source.enabled !== false,
    rules: (Array.isArray(source.rules) ? source.rules : []).map(normalizeRule).slice(0, MAX_RULES),
  };
}

export function filterOpenRouterVisionModels(value: unknown): Model[] {
  const root = objectValue(value);
  const data = Array.isArray(root.data) ? (root.data as ModelApiItem[]) : [];
  return data
    .filter((model) => {
      const inputModalities = model.architecture?.input_modalities;
      const outputModalities = model.architecture?.output_modalities;
      const supportedParameters = model.supported_parameters;
      const inputs = Array.isArray(inputModalities) ? inputModalities.map(String) : [];
      const outputs = Array.isArray(outputModalities) ? outputModalities.map(String) : [];
      const parameters = Array.isArray(supportedParameters)
        ? supportedParameters.map(String)
        : [];
      return typeof model.id === "string" && inputs.includes("image") && outputs.includes("text") &&
        parameters.includes("structured_outputs") && !model.expiration_date;
    })
    .map((model) => ({
      id: String(model.id),
      name: stringValue(model.name, String(model.id)),
      promptPrice: Number.isFinite(Number(model.pricing?.prompt)) ? Number(model.pricing?.prompt) : null,
      completionPrice: Number.isFinite(Number(model.pricing?.completion)) ? Number(model.pricing?.completion) : null,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

async function idToken() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Your session expired. Sign in again.");
  return token;
}
function rectangle(start: Point, end: Point): AiMonitoringZone {
  return {
    x: round(Math.min(start.x, end.x)),
    y: round(Math.min(start.y, end.y)),
    width: round(Math.abs(start.x - end.x)),
    height: round(Math.abs(start.y - end.y)),
  };
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: "grid", gap: 6, color: "#475569", fontSize: 11, fontWeight: 800 }}>{label}{children}</label>;
}

export default function AIMonitoringTab({ camera, onUpdateCamera }: Props) {
  const initial = useMemo(
    () => normalizeAiMonitoringConfig(camera.aiMonitoringConfig),
    [camera.cameraId, camera.aiMonitoringConfig],
  );
  const [config, setConfig] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [selectedId, setSelectedId] = useState(initial.rules[0]?.id || "");
  const [drawing, setDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState<Point[]>([]);
  const [hoverPoint, setHoverPoint] = useState<Point | null>(null);
  const [aspectRatio, setAspectRatio] = useState("16 / 9");
  const [email, setEmail] = useState("");
  const [models, setModels] = useState<Model[]>([]);
  const [modelStatus, setModelStatus] = useState("Loading compatible models...");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testState, setTestState] = useState<AiTestState | null>(null);

  useEffect(() => {
    const next = normalizeAiMonitoringConfig(camera.aiMonitoringConfig);
    setConfig(next);
    setSaved(next);
    setSelectedId(next.rules[0]?.id || "");
    setDrawing(false);
    setDraftPoints([]);
    setHoverPoint(null);
    setTesting(false);
    setTestState(null);
  }, [camera.cameraId, camera.aiMonitoringConfig]);

  useEffect(() => {
    let cancelled = false;
    fetch(MODELS_API, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      })
      .then((payload) => {
        if (cancelled) return;
        const compatible = filterOpenRouterVisionModels(payload);
        setModels(compatible);
        setModelStatus(`${compatible.length} compatible image models available.`);
      })
      .catch(() => {
        if (!cancelled) setModelStatus("Model catalogue unavailable. Enter an exact model ID manually.");
      });
    return () => { cancelled = true; };
  }, []);

  const rule = config.rules.find((item) => item.id === selectedId) || null;
  const dirty = JSON.stringify(config) !== JSON.stringify(saved);
  const visibleZonePoints = rule?.zone ? zonePoints(rule.zone) : [];

  function updateRule(changes: Partial<AiMonitoringRule>) {
    setConfig((current) => ({
      ...current,
      rules: current.rules.map((item) => item.id === selectedId ? { ...item, ...changes } : item),
    }));
  }
  function updateModel(changes: Partial<AiModelConfig>) {
    if (!rule) return;
    updateRule({ modelConfig: { ...rule.modelConfig, ...changes } });
  }
  function updateSchedule(changes: Partial<AiMonitoringSchedule>) {
    if (!rule) return;
    updateRule({ schedule: { ...rule.schedule, ...changes } });
  }
  function toggleScheduleDay(day: AiWeekday) {
    if (!rule) return;
    const days = rule.schedule.days.includes(day)
      ? rule.schedule.days.filter((item) => item !== day)
      : WEEKDAYS.filter((item) => rule.schedule.days.includes(item) || item === day);
    updateSchedule({ days });
  }
  function addRule() {
    if (config.rules.length >= MAX_RULES) return;
    const next = emptyRule(config.rules.length);
    setConfig((current) => ({ ...current, enabled: true, rules: [...current.rules, next] }));
    setSelectedId(next.id);
  }
  function canvasPoint(event: React.PointerEvent<HTMLDivElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: clamp((event.clientX - box.left) / box.width), y: clamp((event.clientY - box.top) / box.height) };
  }
  function startDrawing() {
    if (!rule) return;
    setDrawing(true);
    setDraftPoints([]);
    setHoverPoint(null);
    setIsError(false);
    setMessage(`Click around the exact monitoring area. Add 3-${MAX_POLYGON_POINTS} points, then finish the area.`);
  }
  function cancelDrawing() {
    setDrawing(false);
    setDraftPoints([]);
    setHoverPoint(null);
    setMessage("");
  }
  function finishDrawing() {
    if (!rule) return;
    const zone = polygonZone(draftPoints);
    if (!zone) {
      setIsError(true);
      setMessage("The monitoring polygon needs at least 3 distinct points and must cover a visible area.");
      return;
    }
    updateRule({ zone });
    setDrawing(false);
    setDraftPoints([]);
    setHoverPoint(null);
    setIsError(false);
    setMessage("Flexible monitoring area updated from the current camera thumbnail.");
  }
  function addPolygonPoint(event: React.PointerEvent<HTMLDivElement>) {
    if (!drawing || !rule) return;
    const point = canvasPoint(event);
    if (
      draftPoints.length >= 3 &&
      Math.hypot(point.x - draftPoints[0].x, point.y - draftPoints[0].y) <= 0.025
    ) {
      finishDrawing();
      return;
    }
    if (draftPoints.length >= MAX_POLYGON_POINTS) {
      setIsError(true);
      setMessage(`A monitoring polygon supports up to ${MAX_POLYGON_POINTS} points.`);
      return;
    }
    setIsError(false);
    setDraftPoints((current) => [...current, point]);
  }
  function movePolygonPointer(event: React.PointerEvent<HTMLDivElement>) {
    if (drawing) setHoverPoint(canvasPoint(event));
  }
  function undoPolygonPoint() {
    setDraftPoints((current) => current.slice(0, -1));
    setIsError(false);
  }
  function addEmail() {
    if (!rule) return;
    const normalized = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setIsError(true);
      setMessage("Enter a valid email address.");
      return;
    }
    if (rule.alertEmails.includes(normalized)) return;
    if (rule.alertEmails.length >= MAX_EMAILS) {
      setIsError(true);
      setMessage(`A rule supports up to ${MAX_EMAILS} recipients.`);
      return;
    }
    updateRule({ alertEmails: [...rule.alertEmails, normalized] });
    setEmail("");
    setMessage("");
  }
  function validate() {
    if (!config.enabled) return "";
    const active = config.rules.filter((item) => item.enabled);
    if (!active.length) return "Enable at least one AI monitoring rule.";
    for (const item of active) {
      if (!item.instruction.trim()) return `${item.name} needs a monitoring instruction.`;
      if (!item.zone) return `${item.name} needs a monitoring area.`;
      if (!item.alertEmails.length) return `${item.name} needs at least one alert email.`;
      if (item.schedule.enabled) {
        if (!isValidTimeZone(item.schedule.timezone)) return `${item.name} needs a valid IANA timezone.`;
        if (!item.schedule.days.length) return `${item.name} needs at least one monitoring day.`;
        if (!validClock(item.schedule.start) || !validClock(item.schedule.end) || item.schedule.start === item.schedule.end) {
          return `${item.name} needs different valid schedule start and end times.`;
        }
      }
      if (item.modelConfig.mode === "manual" && !item.modelConfig.primaryModel) {
        return `${item.name} needs a primary AI model.`;
      }
    }
    return "";
  }
  async function save() {
    const validation = validate();
    if (validation) {
      setIsError(true);
      setMessage(validation);
      return;
    }
    setSaving(true);
    setIsError(false);
    setMessage("Saving AI monitoring rules...");
    try {
      const response = await fetch(`${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${await idToken()}`,
        },
        body: JSON.stringify({ aiMonitoringConfig: config }),
      });
      const text = await response.text();
      let payload: any = null;
      try { payload = text ? JSON.parse(text) : null; } catch { payload = null; }
      if (!response.ok) throw new Error(payload?.message || payload?.error || text || response.statusText);
      const persisted = normalizeAiMonitoringConfig(payload?.camera?.aiMonitoringConfig || config);
      setConfig(persisted);
      setSaved(persisted);
      onUpdateCamera({ ...camera, ...(payload?.camera || {}), aiMonitoringConfig: persisted });
      setMessage(`Saved ${persisted.rules.length} AI monitoring rule${persisted.rules.length === 1 ? "" : "s"}.`);
    } catch (cause) {
      setIsError(true);
      setMessage(`Unable to save AI monitoring: ${cause instanceof Error ? cause.message : "Unexpected error"}`);
    } finally {
      setSaving(false);
    }
  }

  async function testRule() {
    if (!rule || testing) return;
    if (dirty) {
      setIsError(true);
      setMessage("Save this rule before testing it.");
      return;
    }
    if (!rule.enabled || !rule.instruction.trim() || !rule.zone) {
      setIsError(true);
      setMessage("The selected rule must be active, saved and have an instruction and area.");
      return;
    }

    setTesting(true);
    setTestState(null);
    setIsError(false);
    setMessage("Test started. Perform the action in view of the camera now; Qumra is collecting chronological frames.");
    try {
      const token = await idToken();
      const startResponse = await fetch(
        `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/ai-monitoring/rules/${encodeURIComponent(rule.id)}/test`,
        {
          method: "POST",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        },
      );
      const startText = await startResponse.text();
      let current: AiTestState = startText ? JSON.parse(startText) : {};
      if (!startResponse.ok) throw new Error(current.error || (current as any).message || startText || startResponse.statusText);
      if (!current.requestId) throw new Error("The test API did not return a request ID");
      setTestState(current);

      for (let attempt = 0; attempt < 40; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const statusResponse = await fetch(
          `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/ai-monitoring/tests/${encodeURIComponent(current.requestId)}`,
          { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } },
        );
        const statusText = await statusResponse.text();
        current = statusText ? JSON.parse(statusText) : {};
        if (!statusResponse.ok) throw new Error(current.error || (current as any).message || statusText || statusResponse.statusText);
        setTestState(current);
        if (current.status === "COMPLETED") {
          setMessage("AI monitoring test completed. No email was sent.");
          return;
        }
        if (current.status === "FAILED") {
          throw new Error(current.error || "The AI monitoring test failed");
        }
        setMessage(
          current.status === "PROCESSING"
            ? "Frames were captured. Qumra AI is analyzing the sequence..."
            : "Waiting for camera frames to be captured...",
        );
      }
      throw new Error("The test did not finish before the portal timeout. Check the backend logs and try again.");
    } catch (cause) {
      setIsError(true);
      setMessage(`Unable to test AI monitoring: ${cause instanceof Error ? cause.message : "Unexpected error"}`);
    } finally {
      setTesting(false);
    }
  }
  return (
    <section style={{ display: "grid", gap: 18, padding: 22, border: "1px solid #e2e8f0", borderRadius: 16, background: "#fff" }}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#7c3aed", fontSize: 11, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase" }}>Qumra AI monitoring</div>
          <h3 style={{ margin: "5px 0 7px", color: "#0f172a" }}>AI monitoring rules</h3>
          <p style={{ margin: 0, maxWidth: 780, color: "#64748b", lineHeight: 1.55 }}>
            Draw a flexible monitoring area on the camera thumbnail, enter the instruction and alert email, then choose a model for each rule.
          </p>
        </div>
        <label style={{ ...button, display: "inline-flex", alignItems: "center", gap: 8, background: "#f8fafc" }}>
          <input type="checkbox" role="switch" checked={config.enabled} onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))} />
          Enable AI monitoring
        </label>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(190px,.5fr) minmax(420px,1.5fr)", gap: 18, opacity: config.enabled ? 1 : 0.58 }}>
        <aside style={{ display: "grid", gap: 9, alignContent: "start" }}>
          <strong style={{ color: "#0f172a", fontSize: 13 }}>Rules</strong>
          {config.rules.map((item, index) => (
            <button key={item.id} type="button" onClick={() => setSelectedId(item.id)} style={{ ...button, display: "grid", gap: 3, textAlign: "left", borderColor: item.id === selectedId ? "#a78bfa" : "#e2e8f0", background: item.id === selectedId ? "#f5f3ff" : "#fff" }}>
              <span>{index + 1}. {item.name}</span>
              <span style={{ fontSize: 10, color: "#64748b" }}>{item.enabled ? "Active" : "Paused"} · {item.zone ? "Area ready" : "No area"}</span>
            </button>
          ))}
          <button type="button" disabled={!config.enabled || config.rules.length >= MAX_RULES} onClick={addRule} style={{ ...button, color: "#6d28d9", borderColor: "#c4b5fd", background: "#f5f3ff" }}>+ Add AI rule</button>
        </aside>

        {!rule ? (
          <div style={{ minHeight: 280, display: "grid", placeItems: "center", border: "1px dashed #cbd5e1", borderRadius: 14, color: "#64748b", fontSize: 12 }}>
            Create or select an AI monitoring rule.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 9, alignItems: "end" }}>
              <Field label="Rule name"><input value={rule.name} maxLength={80} onChange={(event) => updateRule({ name: event.target.value })} style={input} /></Field>
              <label style={{ ...button, display: "inline-flex", alignItems: "center", gap: 7, background: "#f8fafc" }}><input type="checkbox" checked={rule.enabled} onChange={(event) => updateRule({ enabled: event.target.checked })} /> Active</label>
              <button type="button" onClick={() => {
                const remaining = config.rules.filter((item) => item.id !== rule.id);
                setConfig((current) => ({ ...current, rules: remaining }));
                setSelectedId(remaining[0]?.id || "");
              }} style={{ ...button, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" }}>Delete</button>
            </div>

            <div style={{ display: "grid", gap: 9 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <strong style={{ fontSize: 13 }}>Monitoring area</strong>
                  <div style={{ marginTop: 3, color: "#64748b", fontSize: 11 }}>
                    Click points around the exact area on the latest thumbnail from {camera.name || "this camera"}. Only the purple area is sent to AI and included in evidence emails.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {drawing ? (
                    <>
                      <button type="button" disabled={!draftPoints.length} onClick={undoPolygonPoint} style={{ ...button, opacity: draftPoints.length ? 1 : 0.5 }}>Undo point</button>
                      <button type="button" disabled={draftPoints.length < 3} onClick={finishDrawing} style={{ ...button, color: "#fff", borderColor: "#7c3aed", background: "#7c3aed", opacity: draftPoints.length >= 3 ? 1 : 0.5 }}>Finish area</button>
                      <button type="button" onClick={cancelDrawing} style={{ ...button, color: "#b91c1c", borderColor: "#fecaca", background: "#fef2f2" }}>Cancel drawing</button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={startDrawing} style={{ ...button, color: "#6d28d9", borderColor: "#c4b5fd", background: "#f5f3ff" }}>{rule.zone ? "Redraw area" : "Draw area"}</button>
                      <button type="button" disabled={!rule.zone} onClick={() => updateRule({ zone: null })} style={{ ...button, opacity: rule.zone ? 1 : 0.5 }}>Clear</button>
                    </>
                  )}
                </div>
              </div>
              {drawing && (
                <div style={{ color: "#6d28d9", fontSize: 11, fontWeight: 800 }}>
                  {draftPoints.length}/{MAX_POLYGON_POINTS} points · click the first point or Finish area when done
                </div>
              )}
              <div
                data-testid="ai-monitoring-canvas"
                onPointerDown={addPolygonPoint}
                onPointerMove={movePolygonPointer}
                onPointerLeave={() => setHoverPoint(null)}
                style={{ position: "relative", width: "100%", aspectRatio, overflow: "hidden", border: `2px solid ${drawing ? "#7c3aed" : "#cbd5e1"}`, borderRadius: 14, background: "#e2e8f0", cursor: drawing ? "crosshair" : "default", userSelect: "none", touchAction: "none" }}
              >
                {camera.thumbnailUrl ? <img src={camera.thumbnailUrl} alt={`${camera.name || "Camera"} AI monitoring preview`} draggable={false} onLoad={(event) => {
                  const image = event.currentTarget;
                  if (image.naturalWidth && image.naturalHeight) setAspectRatio(`${image.naturalWidth} / ${image.naturalHeight}`);
                }} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill", pointerEvents: "none" }} /> : <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#64748b", fontSize: 12 }}>Latest camera thumbnail is unavailable</div>}
                <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
                  {!drawing && rule.zone && (
                    <polygon points={svgPoints(visibleZonePoints)} fill="rgba(124,58,237,.20)" stroke="#7c3aed" strokeWidth={5} vectorEffect="non-scaling-stroke" />
                  )}
                  {drawing && draftPoints.length >= 3 && (
                    <polygon points={svgPoints(draftPoints)} fill="rgba(124,58,237,.16)" stroke="none" />
                  )}
                  {drawing && draftPoints.length >= 2 && (
                    <polyline points={svgPoints(draftPoints)} fill="none" stroke="#7c3aed" strokeWidth={4} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                  )}
                  {drawing && hoverPoint && draftPoints.length > 0 && (
                    <line
                      x1={draftPoints[draftPoints.length - 1].x * 1000}
                      y1={draftPoints[draftPoints.length - 1].y * 1000}
                      x2={hoverPoint.x * 1000}
                      y2={hoverPoint.y * 1000}
                      stroke="#7c3aed"
                      strokeWidth={3}
                      strokeDasharray="8 8"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  {drawing && draftPoints.map((point, index) => (
                    <circle key={`${point.x}-${point.y}-${index}`} cx={point.x * 1000} cy={point.y * 1000} r={index === 0 ? 11 : 8} fill={index === 0 ? "#5b21b6" : "#7c3aed"} stroke="#fff" strokeWidth={3} vectorEffect="non-scaling-stroke" />
                  ))}
                </svg>
                {rule.zone && !drawing && <span style={{ position: "absolute", top: 8, left: 8, padding: "3px 7px", borderRadius: 7, color: "#fff", background: "rgba(76,29,149,.88)", fontSize: 10, fontWeight: 900, pointerEvents: "none" }}>AI monitoring area</span>}
              </div>
            </div>

            <Field label="What should the AI monitor inside this area?">
              <textarea rows={5} value={rule.instruction} maxLength={2000} placeholder="Example: Notify me if a person hits, kicks, or appears to be trying to break the storefront glass." onChange={(event) => updateRule({ instruction: event.target.value })} style={{ ...input, resize: "vertical", lineHeight: 1.5 }} />
            </Field>

            <div style={{ display: "grid", gap: 8 }}>
              <strong style={{ fontSize: 13 }}>Alert recipients</strong>
              <div style={{ display: "flex", gap: 8 }}><input type="email" value={email} placeholder="security@example.com" onChange={(event) => setEmail(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addEmail(); } }} style={input} /><button type="button" onClick={addEmail} style={button}>Add email</button></div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{rule.alertEmails.length ? rule.alertEmails.map((item) => <button key={item} type="button" title="Remove recipient" onClick={() => updateRule({ alertEmails: rule.alertEmails.filter((value) => value !== item) })} style={{ ...button, minHeight: 32, padding: "5px 9px", color: "#1d4ed8", borderColor: "#bfdbfe", background: "#eff6ff" }}>{item} ×</button>) : <span style={{ color: "#64748b", fontSize: 11 }}>No recipients added.</span>}</div>
            </div>

            <div style={{ display: "grid", gap: 10, padding: 15, border: "1px solid #cbd5e1", borderRadius: 14, background: "#f8fafc" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <strong style={{ color: "#0f172a", fontSize: 13 }}>Monitoring schedule</strong>
                  <div style={{ marginTop: 3, color: "#64748b", fontSize: 11 }}>
                    Choose when this rule is allowed to analyze motion events.
                  </div>
                </div>
                <label style={{ ...button, display: "inline-flex", alignItems: "center", gap: 7, background: "#fff" }}>
                  <input
                    type="checkbox"
                    checked={rule.schedule.enabled}
                    onChange={(event) => updateSchedule({ enabled: event.target.checked })}
                  />
                  Use custom schedule
                </label>
              </div>

              {!rule.schedule.enabled ? (
                <div style={{ color: "#166534", fontSize: 11, fontWeight: 800 }}>Always active · 24/7</div>
              ) : (
                <>
                  <Field label="Timezone">
                    <input
                      list={`ai-timezones-${rule.id}`}
                      value={rule.schedule.timezone}
                      onChange={(event) => updateSchedule({ timezone: event.target.value })}
                      placeholder="America/Toronto"
                      style={input}
                    />
                  </Field>
                  <datalist id={`ai-timezones-${rule.id}`}>
                    {[
                      "America/Toronto",
                      "America/Vancouver",
                      "America/Edmonton",
                      "America/Winnipeg",
                      "America/Halifax",
                      "America/St_Johns",
                      "America/New_York",
                      "America/Chicago",
                      "America/Denver",
                      "America/Los_Angeles",
                      "Europe/London",
                      "Asia/Riyadh",
                      "Asia/Dubai",
                      "Asia/Shanghai",
                      "UTC",
                    ].map((timezone) => <option key={timezone} value={timezone} />)}
                  </datalist>

                  <div style={{ display: "grid", gap: 7 }}>
                    <strong style={{ color: "#475569", fontSize: 11 }}>Monitoring days</strong>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {WEEKDAYS.map((day) => {
                        const active = rule.schedule.days.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            aria-pressed={active}
                            onClick={() => toggleScheduleDay(day)}
                            style={{
                              ...button,
                              minHeight: 30,
                              padding: "4px 8px",
                              color: active ? "#fff" : "#475569",
                              borderColor: active ? "#7c3aed" : "#cbd5e1",
                              background: active ? "#7c3aed" : "#fff",
                            }}
                          >
                            {day.slice(0, 1) + day.slice(1).toLowerCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(140px,1fr))", gap: 10 }}>
                    <Field label="Start monitoring">
                      <input
                        type="time"
                        value={rule.schedule.start}
                        onChange={(event) => updateSchedule({ start: event.target.value })}
                        style={input}
                      />
                    </Field>
                    <Field label="Stop monitoring">
                      <input
                        type="time"
                        value={rule.schedule.end}
                        onChange={(event) => updateSchedule({ end: event.target.value })}
                        style={input}
                      />
                    </Field>
                  </div>

                  <span style={{ color: "#64748b", fontSize: 10 }}>
                    Overnight schedules are supported. For example, Mon 18:00 → 06:00 remains active until Tue 06:00.
                  </span>
                </>
              )}
            </div>

            <div style={{ display: "grid", gap: 12, padding: 15, border: "1px solid #ddd6fe", borderRadius: 14, background: "#faf5ff" }}>
              <div><strong style={{ color: "#5b21b6", fontSize: 13 }}>AI model</strong><div style={{ marginTop: 3, color: "#64748b", fontSize: 11 }}>Choose how Qumra AI evaluates this rule. Recommended mode automatically selects a compatible model.</div></div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800 }}><input type="radio" name={`model-${rule.id}`} checked={rule.modelConfig.mode === "recommended"} onChange={() => updateModel({ mode: "recommended", primaryModel: null, fallbackModels: [] })} /> Qumra recommended</label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 800 }}><input type="radio" name={`model-${rule.id}`} checked={rule.modelConfig.mode === "manual"} onChange={() => updateModel({ mode: "manual" })} /> Choose manually</label>
              </div>
              {rule.modelConfig.mode === "manual" && <>
                <Field label="Primary model"><input list="openrouter-models" value={rule.modelConfig.primaryModel || ""} placeholder="provider/model-slug" onChange={(event) => updateModel({ primaryModel: event.target.value.trim() || null })} style={input} /></Field>
                <Field label="Fallback model (optional)"><input list="openrouter-models" value={rule.modelConfig.fallbackModels[0] || ""} placeholder="provider/backup-model-slug" onChange={(event) => updateModel({ fallbackModels: event.target.value.trim() ? [event.target.value.trim()] : [] })} style={input} /></Field>
                <datalist id="openrouter-models">{models.map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}</datalist>
                <span style={{ color: "#64748b", fontSize: 10 }}>{modelStatus}</span>
              </>}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(160px,1fr))", gap: 10 }}>
                <Field label="Routing preference"><select value={rule.modelConfig.routingPreference} onChange={(event) => updateModel({ routingPreference: event.target.value as RoutingPreference })} style={input}><option value="balanced">Balanced</option><option value="lowest_cost">Lowest cost</option><option value="fastest">Fastest</option><option value="highest_accuracy">Highest accuracy</option></select></Field>
                <Field label="Frames per analysis"><select value={rule.frameCount} onChange={(event) => updateRule({ frameCount: Number(event.target.value) })} style={input}>{Array.from({ length: 12 }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count} {count === 1 ? "frame" : "frames"}</option>)}</select></Field>
              </div>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#475569", fontSize: 11, fontWeight: 800 }}><input type="checkbox" checked={rule.modelConfig.allowFallback} onChange={(event) => updateModel({ allowFallback: event.target.checked })} /> Allow fallback when the selected model is unavailable</label>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(140px,1fr))", gap: 10 }}>
              <Field label="Minimum confidence"><select value={rule.minimumConfidence} onChange={(event) => updateRule({ minimumConfidence: Number(event.target.value) })} style={input}><option value={0.6}>60%</option><option value={0.7}>70%</option><option value={0.75}>75% · balanced</option><option value={0.8}>80%</option><option value={0.9}>90%</option></select></Field>
              <Field label="Repeat cooldown"><select value={rule.cooldownSeconds} onChange={(event) => updateRule({ cooldownSeconds: Number(event.target.value) })} style={input}><option value={0}>No cooldown</option><option value={120}>2 minutes</option><option value={300}>5 minutes</option><option value={600}>10 minutes</option><option value={1800}>30 minutes</option></select></Field>
              <Field label="Alert priority"><select value={rule.severity} onChange={(event) => updateRule({ severity: event.target.value as AiMonitoringRule["severity"] })} style={input}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></Field>
            </div>
          </div>
        )}
      </div>

      {testState?.result && (
        <div style={{ display: "grid", gap: 9, padding: 15, border: "1px solid " + (testState.result.notify ? "#c4b5fd" : "#cbd5e1"), borderRadius: 12, background: testState.result.notify ? "#faf5ff" : "#f8fafc" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <strong style={{ color: testState.result.notify ? "#6d28d9" : "#334155" }}>
              {testState.result.ruleMatched ? "Rule matched" : "Rule not matched"}
            </strong>
            <span style={{ color: "#64748b", fontSize: 11 }}>
              {Math.round(Number(testState.result.confidence || 0) * 100)}% confidence · {testState.result.frameCount || rule?.frameCount || 6} frames
            </span>
          </div>
          <div style={{ color: "#0f172a", fontSize: 12, lineHeight: 1.55 }}>{testState.result.summary}</div>
          {Boolean(testState.result.observations?.length) && (
            <ul style={{ margin: 0, paddingLeft: 18, color: "#475569", fontSize: 11, lineHeight: 1.55 }}>
              {testState.result.observations?.map((observation, index) => <li key={`${index}-${observation}`}>{observation}</li>)}
            </ul>
          )}
          <div style={{ color: "#64748b", fontSize: 10 }}>
            Model: {testState.result.modelUsed || "Unknown"}{testState.result.fallbackUsed ? " · fallback used" : ""} · {testState.result.processingTimeMs || 0} ms · test mode only
          </div>
        </div>
      )}
      {message && <div role={isError ? "alert" : "status"} style={{ padding: "10px 12px", border: `1px solid ${isError ? "#fecaca" : "#bbf7d0"}`, borderRadius: 10, color: isError ? "#b91c1c" : "#166534", background: isError ? "#fef2f2" : "#f0fdf4", fontSize: 12, fontWeight: 700 }}>{message}</div>}
      <footer style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
        <span style={{ marginRight: "auto", color: "#64748b", fontSize: 11 }}>Test mode analyzes camera frames and never sends email.</span>
        <button type="button" disabled={!rule || dirty || saving || testing} onClick={() => void testRule()} style={{ ...button, color: "#6d28d9", borderColor: "#c4b5fd", background: "#faf5ff", opacity: rule && !dirty && !saving && !testing ? 1 : 0.5 }}>{testing ? "Testing..." : "Test AI Monitoring"}</button>
        <button type="button" disabled={!dirty || saving || testing} onClick={() => { setConfig(saved); setSelectedId(saved.rules[0]?.id || ""); setMessage(""); }} style={{ ...button, opacity: dirty && !saving ? 1 : 0.5 }}>Reset</button>
        <button type="button" disabled={!dirty || saving || testing} onClick={() => void save()} style={{ ...button, color: "#fff", background: "#7c3aed", borderColor: "#7c3aed", opacity: dirty && !saving ? 1 : 0.55 }}>{saving ? "Saving..." : "Save AI monitoring"}</button>
      </footer>
    </section>
  );
}
